import { apiCall } from '../../../services/apiUtil';

/**
 * Basketball scoring against the unified games API.
 *
 * Separate from basketballGameApi.js, which talks to the legacy
 * /basketball-games routes and is still used by the older game views. Scoring
 * now goes through /games/:id/basketball/* like every other sport, where each
 * action is appended to a log the server owns — so an undo is a real delete
 * rather than a client-side stack that a refresh throws away.
 */

// Same reasoning as pickleball's: these are small interactive calls made while
// someone is standing courtside. A stuck spinner is worse than a fast failure
// they can retry, so cap the wait rather than inheriting the browser default.
const SCORING_TIMEOUT_MS = 8000;
const withTimeout = (options = {}) => ({ ...options, signal: AbortSignal.timeout(SCORING_TIMEOUT_MS) });

export const basketballScoringAPI = {
  getById: async (gameId) => apiCall(`/games/${gameId}`),

  /**
   * Record one action.
   * @param {object} action
   * @param {1|2}    action.team            1 = side A, 2 = side B
   * @param {string} action.eventType       free_throw | field_goal | three_pointer |
   *                                        rebound | assist | steal | block | turnover | foul
   * @param {boolean} [action.isMade]       false for a missed shot — logged, scores nothing
   * @param {string} [action.playerId]
   * @param {string} [action.assistPlayerId] credited only on a made shot
   * @param {number} [action.points]        overrides the shot's default (and-one, four-point play)
   */
  recordEvent: async (gameId, action) =>
    apiCall(`/games/${gameId}/basketball/events`, withTimeout({
      method: 'POST',
      body: JSON.stringify(action),
    })),

  undoLast: async (gameId) =>
    apiCall(`/games/${gameId}/basketball/events/last`, withTimeout({ method: 'DELETE' })),

  setPeriod: async (gameId, period) =>
    apiCall(`/games/${gameId}/basketball/period`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ period }),
    })),

  /** Choose who is on court for a side; the rest of the squad stays as subs. */
  setLineup: async (gameId, { side, playerIds }) =>
    apiCall(`/games/${gameId}/basketball/lineup`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ side, playerIds }),
    })),

  swapPlayer: async (gameId, { side, oldPlayerId, newPlayer }) =>
    apiCall(`/games/${gameId}/basketball/swap-player`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ side, oldPlayerId, newPlayer }),
    })),

  completeGame: async (gameId) =>
    apiCall(`/games/${gameId}/basketball/game-complete`, withTimeout({ method: 'POST' })),

  /** Skip play-by-play: set both final scores and complete in one action. */
  submitFinalScore: async (gameId, { participantAScore, participantBScore }) =>
    apiCall(`/games/${gameId}/basketball/final-score`, withTimeout({
      method: 'PATCH',
      body: JSON.stringify({ participantAScore, participantBScore }),
    })),

  reset: async (gameId) =>
    apiCall(`/games/${gameId}/basketball/reset`, withTimeout({ method: 'POST' })),
};

export default basketballScoringAPI;
